import { Bell, Plus } from "lucide-react";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { Can } from "@/lib/authorization";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useDialogRouterSimple as useDialogRouter } from "@/lib/dialog/use-dialog-router-simple";
import { NewCalendarContent } from "../components/new-calendar-content";
import { PauseBookingsButton } from "../components/pause-bookings-button";
import { PremiumDateBanner } from "../components/premium-date-banner";
import { ResponsiveDatePicker } from "../components/responsive-date-picker";
import { UnifiedViewConfigDropdown } from "../components/unified-view-config-dropdown";
import { useCalendarTools } from "../hooks/use-calendar-tools";
import { useConfirmationsAppointments } from "../hooks/use-confirmations-appointments";
import { useNewCalendarUIStore } from "../store/use-new-calendar-ui-store";

export function NewCalendarPage() {
  // Register calendar tools with assistant-ui
  useCalendarTools();

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("newCalendar");

  const viewMode = useNewCalendarUIStore((state) => state.viewMode);
  const currentDate = useNewCalendarUIStore((state) => state.currentDate);
  const setCurrentDate = useNewCalendarUIStore((state) => state.setCurrentDate);

  // Get confirmation appointments count for badge
  const { appointments: confirmationAppointments } = useConfirmationsAppointments();
  const confirmationCount = confirmationAppointments.length;

  const { openDialog } = useDialogRouter();

  const handleCreateAppointment = () => {
    openDialog({ type: "appointment-create" });
  };

  const handleNavigateToConfirmations = () => {
    navigate("/confirmations");
  };

  // Generate dynamic subtitle based on view mode and date
  const subtitle = useMemo(() => {
    const dateStr = currentDate.toFormat("dd-MM-yyyy");

    switch (viewMode) {
      case "list":
        return t("subtitles.list");
      case "daily":
        return t("subtitles.daily", { date: dateStr });
      case "3-day":
        return t("subtitles.threeDays", { date: dateStr });
      case "week":
        return t("subtitles.week", {
          startDate: currentDate.startOf("week").toFormat("dd-MM"),
          endDate: currentDate.endOf("week").toFormat("dd-MM-yyyy"),
        });
      case "month":
        return t("subtitles.month", {
          month: currentDate.toFormat("MM-yyyy"),
        });
      case "worker":
        return t("subtitles.worker", { date: dateStr });
      default:
        return t("subtitles.default");
    }
  }, [viewMode, currentDate, t]);

  const handleDateChange = (newDate: DateTime) => {
    setCurrentDate(newDate);
  };

  return (
    <>
      <BaseNavigationBar
        title={t("title")}
        subtitle={subtitle}
        leadElement={
          isMobile ? (
            <div className="flex items-center gap-2">
              <UnifiedViewConfigDropdown isMobile={true} />
              <PauseBookingsButton isMobile={true} />
              {/* Show confirmation badge after unified dropdown on mobile */}
              {confirmationCount > 0 && (
                <Button
                  key="confirmations-badge-mobile"
                  variant="ghost"
                  size="sm"
                  onClick={handleNavigateToConfirmations}
                  className="relative"
                  title={t(
                    "buttons.confirmAppointments",
                    `${confirmationCount} appointments waiting for confirmation`
                  )}
                >
                  <Bell className="h-4 w-4" />
                  <Badge
                    variant="destructive"
                    className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center text-xs p-0"
                  >
                    {confirmationCount > 99 ? "99+" : confirmationCount}
                  </Badge>
                </Button>
              )}
            </div>
          ) : undefined
        }
        trailingElements={[
          // Show unified dropdown only on desktop (mobile version is in leadElement)
          !isMobile ? (
            <UnifiedViewConfigDropdown key="unified-view-config" isMobile={false} />
          ) : null,
          // Quick action to pause new online bookings (relocated from Settings)
          !isMobile ? <PauseBookingsButton key="pause-bookings" isMobile={false} /> : null,
          // Confirmation badge button - shown when there are appointments to confirm (desktop only, mobile is in leadElement)
          !isMobile && confirmationCount > 0 ? (
            <Button
              key="confirmations-badge"
              variant="ghost"
              size="default"
              onClick={handleNavigateToConfirmations}
              className="relative gap-2"
              title={t(
                "buttons.confirmAppointments",
                `${confirmationCount} appointments waiting for confirmation`
              )}
            >
              <Bell className="h-4 w-4" />
              <span className="font-medium">{t("buttons.confirm", "Confirm")}</span>
              <Badge variant="destructive" className="ml-1 px-2 py-0.5 text-xs">
                {confirmationCount > 99 ? "99+" : confirmationCount}
              </Badge>
            </Button>
          ) : null,
          <Can I="create" a="Appointment" key="new-appointment">
            <Button
              size={isMobile ? "sm" : "default"}
              onClick={handleCreateAppointment}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              {!isMobile && t("buttons.newAppointment")}
            </Button>
          </Can>,
        ].filter(Boolean)}
        bottomElement={
          <ResponsiveDatePicker
            viewMode={viewMode}
            currentDate={currentDate}
            onDateChange={handleDateChange}
          />
        }
      />

      <PageContent className={viewMode === "list" ? undefined : "flex flex-col overflow-hidden"}>
        {viewMode === "list" ? (
          // List view uses default page layout with normal scrolling
          <div className="space-y-6">
            <PremiumDateBanner selectedDate={currentDate} />
            <ErrorAlert error={null} />
            <NewCalendarContent viewMode={viewMode} currentDate={currentDate} />
          </div>
        ) : (
          // Calendar views use full height layout with internal scrolling
          <div className="flex flex-col h-full">
            {/* Premium Date Banner and Error Alert - fixed height items */}
            <div className="flex-shrink-0 space-y-6">
              <PremiumDateBanner selectedDate={currentDate} />
              <ErrorAlert error={null} />
            </div>

            {/* Calendar Content - takes remaining space */}
            <div className="flex-1 h-0 py-2">
              <NewCalendarContent viewMode={viewMode} currentDate={currentDate} />
            </div>
          </div>
        )}
      </PageContent>
    </>
  );
}
